'use client'

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'

export type AnimatedListItem = { text: string; id?: string | null }

type Props = {
  label?: string | null
  items: AnimatedListItem[]
  variant: 'light' | 'dark'
}

const STAGGER_MS = 70
const STAGGER_CAP = 6

export const AnimatedList: React.FC<Props> = ({ label, items, variant }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const labelClass = variant === 'dark' ? 'text-brand-dark' : 'text-black'
  const dividerClass = variant === 'dark' ? 'bg-brand-dark' : 'bg-civo-cloud'
  const bulletTextClass = variant === 'dark' ? 'text-civo-white' : 'text-black/70'

  return (
    <div ref={ref} className="flex flex-col gap-6">
      {label && (
        <div className="flex flex-col gap-3">
          <h3 className={cn('subheading-xsmall', labelClass)}>{label}</h3>
          <div
            className={cn(
              'h-px w-full origin-left transition-transform duration-700 ease-out',
              'motion-reduce:transition-none motion-reduce:scale-x-100',
              dividerClass,
              inView ? 'scale-x-100' : 'scale-x-0',
            )}
          />
        </div>
      )}
      <ul className={cn('flex flex-col gap-3 body-large', bulletTextClass)}>
        {items.map((item, i) => (
          <li
            key={item.id ?? i}
            style={{ transitionDelay: inView ? `${Math.min(i, STAGGER_CAP) * STAGGER_MS}ms` : '0ms' }}
            className={cn(
              'flex gap-3 transition-[opacity,transform] duration-500 ease-out',
              'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
            )}
          >
            <span aria-hidden="true">•</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
